import React, { useLayoutEffect, useRef, useState } from 'react';

import useResizeObserver from '@react-hook/resize-observer';

const isWiderThanParent = (parentWidth: number, childWidth: number) => {
  // note: use Math.abs here to avoid flickering because layout changed by 1px
  return parentWidth < childWidth && Math.abs(parentWidth - childWidth) > 10;
};

export const useResponsiveWidth = (
  offset = 0,
): [
  React.RefObject<HTMLDivElement>,
  React.RefObject<HTMLDivElement>,
  boolean,
] => {
  const ref = useRef<HTMLDivElement>(null);
  const measuredRef = useRef<HTMLDivElement>(null);
  const [widerThanParent, setWiderThanParent] = useState(false);
  const [measuredWidth, setMeasuredWidth] = useState<number>(0);

  const getWidth = () => {
    return measuredRef.current
      ? measuredRef.current.getBoundingClientRect().width
      : null;
  };

  const handleInit = () => {
    const width = getWidth();

    if (width) {
      setMeasuredWidth(width);

      const wider = isWiderThanParent(
        ref?.current?.clientWidth || window.innerWidth,
        width + offset,
      );
      setWiderThanParent(wider);

      if (ref.current) {
        if (wider) {
          ref.current.style.setProperty('transition', 'opacity 0.1s ease-in');
        }
        ref.current.style.setProperty('opacity', '1');
      }
    }
  };

  useLayoutEffect(() => {
    handleInit();
  }, [measuredRef]);

  useResizeObserver(ref, entry => {
    if (measuredWidth) {
      setWiderThanParent(
        isWiderThanParent(entry.contentRect.width, measuredWidth + offset),
      );
    } else if (measuredRef.current) {
      handleInit();
    }
  });

  return [ref, measuredRef, widerThanParent];
};
